import React from 'react';
import { PaymentCheckout } from './PaymentCheckout';
import {
  FCWithChildren,
  serverPricingToClientPriceAndCurrencyString,
  serverTimelineTypeToClientTimelineString,
} from '@wix/challenges-web-library';
import { ApplyCouponToOrderResponse } from '@wix/ambassador-challenges-v1-participant/types';

import { withLocation } from '../../../../contexts/Location/withLocation';
import { ILocationProviderProps } from '../../../../contexts/Location/LocationProvider';
import { ButtonNames } from '../../../../contexts/main/biInterfaces';

import {
  useBi,
  useTranslation,
  useExperiments,
  useEnvironment,
} from '@wix/yoshi-flow-editor';
import { memberWebAppButtonClick as memberWebAppButtonClickV2 } from '@wix/bi-logger-challenges-member-web/v2';
import {
  IChallengeContext,
  withChallengeData,
} from '../../../../contexts/storage-contexts/Challenge';
import { usePaymentControllerData } from '../../hooks/UsePaymentControllerData';
import { useOrderPrice } from '../../../../hooks/useOrderPrice';
import { useUser } from '../../../../contexts/User/UserContext';
import { getParticipantOrderId } from '../../../../selectors/participants';
import { useLocation } from '../../../../contexts/Location/LocationContext';
import { Challenges } from '../../../../editor/types/Experiments';

export interface PaymentCheckoutContainerProps {
  onCouponApplied(coupon: ApplyCouponToOrderResponse): void;
  isPaymentStarted?: boolean;
}

type IPaymentCheckoutContainerProps = IChallengeContext &
  ILocationProviderProps &
  PaymentCheckoutContainerProps & { lng: string };

export const PaymentCheckoutContainer: FCWithChildren<PaymentCheckoutContainerProps> =
  withLocation(
    withChallengeData((props: IPaymentCheckoutContainerProps) => {
      const { t } = useTranslation();
      const { isViewer } = useEnvironment();
      const experiments = useExperiments();
      const challenge = props.challengeData?.challenge;
      const propsFromController = usePaymentControllerData();
      const { participant } = useUser();
      const { query } = useLocation();
      const [manualCouponRemoved, setManualCouponRemoved] =
        React.useState(false);

      const orderId = getParticipantOrderId(participant) || query.orderId;
      const isOrderServiceEnabled = experiments.experiments.enabled(
        Challenges.readPriceFromOrderService,
      );

      // Fetch order price using the custom hook
      const orderPrice = useOrderPrice(
        orderId,
        isOrderServiceEnabled && isViewer,
      );

      if (!challenge) {
        return null;
      }

      const durationString = serverTimelineTypeToClientTimelineString(
        challenge?.settings?.timelineType as any,
        props.lng,
        t,
        'challenge-card.duration-string.ongoing',
        `challenge-card.duration-string.flexible.days_icu`,
        `challenge-card.duration-string.flexible.weeks_icu`,
        'challenge-card.duration-string.no-limit',
      );

      const bi = useBi();

      // Calculate price to display and create appliedCoupon object if needed
      let displayPrice: string;
      let appliedCouponFromOrder: ApplyCouponToOrderResponse | null = null;

      // Check if we should use order price:
      // Only use it when order service is enabled AND there's a coupon in the order
      // AND the user hasn't manually removed the coupon
      const shouldUseOrderPrice =
        isOrderServiceEnabled &&
        orderPrice?.hasCoupon &&
        orderPrice?.originalAmount &&
        !manualCouponRemoved;

      if (shouldUseOrderPrice) {
        // Create the appliedCoupon object for the breakdown
        appliedCouponFromOrder = {
          subTotal: orderPrice.originalAmount!,
          discount: orderPrice.discount || '0',
          total: orderPrice.amount,
          couponId: orderPrice.couponId || '',
        };

        // Format the discounted price from order service
        const pricingFromOrder = {
          singlePayment: {
            price: {
              amount: orderPrice.amount,
              currency: orderPrice.currency,
            },
          },
        };
        displayPrice = serverPricingToClientPriceAndCurrencyString(
          pricingFromOrder as any,
        );
      } else {
        // No coupon or order service disabled - use original price from challenge settings
        displayPrice = serverPricingToClientPriceAndCurrencyString(
          challenge?.settings?.pricing as any,
        );
      }

      return (
        <PaymentCheckout
          hasCouponsForPrograms={propsFromController.hasCouponsForPrograms}
          showOneAppInfo={async () => {
            await bi.report(
              memberWebAppButtonClickV2({
                buttonName: ButtonNames.OneAppInfo,
              }),
            );

            props.goToExternalUrl('https://www.wix.com/mobile/wix-app');
          }}
          title={
            // @ts-expect-error
            challenge?.mocked
              ? t(challenge?.settings?.description?.title)
              : challenge?.settings?.description?.title
          }
          price={displayPrice}
          duration={durationString}
          onCouponApplied={(coupon) => {
            // Track when coupon is manually removed
            if (coupon === null) {
              setManualCouponRemoved(true);
            } else {
              setManualCouponRemoved(false);
            }
            props.onCouponApplied(coupon);
          }}
          isPaymentStarted={props.isPaymentStarted}
          initialAppliedCoupon={appliedCouponFromOrder}
        />
      );
    }),
  );
